import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import SectionWatermark from '@/components/SectionWatermark';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className={className}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.7s ease-out ${delay}ms` }}>
      {children}
    </div>
  );
}

export default function BlogPage() {
  const [data, setData] = useState({ posts: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    fetchCached(`/blog?page=${page}&limit=9`, { ttlMs: 2 * 60 * 1000 }).then((result) => {
      setData(result);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Hero */}
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <span className="text-white/85 text-xs font-bold uppercase tracking-[0.16em]">Million Stars Community Ministry</span>
          <h1 className="mscm-hero-title mt-4 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Blog &amp; Articles
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg">
            Insights, reflections, and updates from our ministry
          </p>
        </div>
      </section>

      {/* Posts Grid */}
      <SectionWatermark
        className="mscm-section bg-white"
        contentClassName="max-w-7xl mx-auto px-6 md:px-8"
        watermarkClassName="mscm-watermark-strong"
        opacity={0.18}
      >
        {loading && data.posts.length === 0 ? (
          <AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid-loading">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="mscm-public-card overflow-hidden">
                  <div className="mscm-public-skeleton aspect-[16/10]" />
                  <div className="p-6">
                    <div className="mscm-public-skeleton h-3 w-24 rounded mb-4" />
                    <div className="mscm-public-skeleton h-6 w-4/5 rounded mb-3" />
                    <div className="mscm-public-skeleton h-4 w-full rounded mb-2" />
                    <div className="mscm-public-skeleton h-4 w-5/6 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        ) : data.posts.length === 0 ? (
          <AnimatedSection>
            <div className="mscm-premium-card mx-auto max-w-xl p-10 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF4FF] text-[#1E4FA1] mb-6">
                <BookOpen size={30} />
              </div>
              <span className="mscm-eyebrow text-[#1E4FA1]">Coming Soon</span>
              <h2 className="mt-3 text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Articles on the way
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#64748B] max-w-sm mx-auto">
                Reflections and ministry updates will be shared here as they are published.
              </p>
            </div>
          </AnimatedSection>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {data.posts.map((post, i) => (
                <AnimatedSection key={post.id} delay={i * 60}>
                  <Link
                    to={`/blog/${post.id}`}
                    data-testid={`blog-card-${post.id}`}
                    className="mscm-public-card group overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30 block h-full"
                  >
                    {post.image_path ? (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={resolveImageUrl(post.image_path)}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] bg-[#EBF2FA] flex items-center justify-center">
                        <BookOpen size={40} className="text-[#1E4FA1]/30" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-[#94A3B8] text-xs mb-3">
                        <Calendar size={14} />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-2 line-clamp-2 group-hover:text-[#1E4FA1] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {post.title}
                      </h3>
                      <p className="text-[#475569] text-sm line-clamp-3 leading-relaxed">
                        {post.content?.substring(0, 150)}...
                      </p>
                      <div className="mt-4 flex items-center gap-1 text-[#1E4FA1] text-sm font-medium">
                        Read More <ArrowRight size={14} />
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>

            {/* Pagination */}
            {data.pages > 1 && (
              <div className="mt-12 flex justify-center gap-2" data-testid="blog-pagination">
                {Array.from({ length: data.pages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    data-testid={`blog-page-${p}`}
                    onClick={() => setPage(p)}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30 ${
                      p === page
                        ? 'bg-[#1E4FA1] text-white shadow-[0_8px_20px_rgba(30,79,161,0.25)]'
                        : 'bg-[#F8FAFC] text-[#475569] hover:bg-[#EBF2FA] hover:text-[#1E4FA1]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </SectionWatermark>
    </div>
  );
}
